import api, { IPaginationResponse } from 'config/axios';
import AppService from 'common/app-service';
import {
  ICarrierBaseInterest,
  ICarrierBranch,
  ICarrierPartner,
  ICarrierRoot,
  ICarrierSecondaryOperation,
  ICarriersRootFilters,
  ICreditLimit,
} from 'common/carrier.interface';
import { IUserRegistry } from 'common/user.interface';
import { cnpjFormatter } from 'utils/formatters';

interface IRootAndBranch {
  root?: Partial<ICarrierRoot>;
  branch: Partial<ICarrierBranch>;
}

export default class CarrierService extends AppService {
  constructor() {
    super('carriers', api);
  }

  async getCarriersRoot(page: number, limit = 10, filters?: Partial<ICarriersRootFilters>): Promise<IPaginationResponse<ICarrierRoot>> {
    const response = await this.api.get(this.root(''), { params: { page, limit, ...(filters || {}) } });

    return response.data;
  }

  async getCarrierRoot(id: number): Promise<ICarrierRoot> {
    const response = await this.api.get(this.root(`root/${id}`));
    return response.data;
  }

  async getCarrierBranch(id: number): Promise<ICarrierBranch> {
    const response = await this.api.get(this.root(`branch/${id}`));
    return response.data;
  }

  async getCarrierRootBy(documentNumber?: string): Promise<any> {
    const response = await this.api.get(this.root(''), {
      params: {
        documentNumber: cnpjFormatter(documentNumber).rawValue,
      },
    });
    return response.data[0] || {};
  }

  async createCarrier(body: IRootAndBranch): Promise<IRootAndBranch> {
    const response = await this.api.post(this.root(''), body);
    return response.data;
  }

  async updateRootAndBranch(id: number | string, { email, documentNumber, ...params }: any): Promise<any> {
    const response = await this.api.put(this.root(id), {
      branch: {
        documentNumber,
      },
      root: {
        email,
        documentNumber,
        ...params,
      },
    });
    return response.data;
  }

  async updateRoot(
    carrierRootId,
    { baseInterest, dailyCreditLimit, totalCreditLimit, maxDueDays, contractEnabled, punitiveFee, ...rest }: Partial<ICarrierRoot>
  ): Promise<Partial<ICarrierRoot>> {
    const response = await this.api.patch(this.root(`root/${carrierRootId}`), {
      baseInterest,
      dailyCreditLimit: Number(dailyCreditLimit) || undefined,
      totalCreditLimit: Number(totalCreditLimit) || undefined,
      maxDueDays: Number(maxDueDays) || undefined,
      contractEnabled,
      punitiveFee,
      ...(rest || {}),
    });
    return response.data;
  }

  async createPartner({ carrierRootId, ...params }: ICarrierPartner): Promise<IUserRegistry> {
    const response = await this.api.post(this.root(`${carrierRootId}/partners`), params);
    return response.data;
  }

  async getPartners(carrierRootId: number): Promise<IPaginationResponse<IUserRegistry>> {
    const response = await this.api.get(this.root(`${carrierRootId}/partners`));
    return response.data;
  }

  async getLimits(): Promise<ICreditLimit> {
    const response = await this.api.get(this.root('credit-limits'));
    return response.data;
  }

  async getBaseInterest(): Promise<ICarrierBaseInterest> {
    const response = await this.api.get(this.root('base-interest'));
    return response.data;
  }

  async getCarriersSecondaryOperation(): Promise<ICarrierSecondaryOperation[]> {
    const response = await this.api.get(this.root('available/secondary_operation'));
    return response.data;
  }

  async getCarrierRootLastUpdateLog(carrierRootId: number): Promise<any> {
    const response = await this.api.get(this.root(`root/${carrierRootId}/last-update-log`));
    return response.data;
  }
}
